import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AbstractSheetController} from '@mintdata-wsp/common/controllers/spreadsheet/AbstractSheetController';
import {AbstractSheetSettingsController} from '@mintdata-wsp/common/controllers/spreadsheet/AbstractSheetSettingsController';
import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {MeshCellSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {NamedRange, RangeSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NamedRange';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {SpreadsheetRuntimeState} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetRuntimeState';
import {RepeaterFixation} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {
    FramePathElements,
    Repeater
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {EnumUtils} from '@mintdata-wsp/common/util/EnumUtil';
import {NamedRangeId, SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {FramePath, IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import bind from 'bind-decorator';
import {action} from 'mobx';
import _times from 'lodash/times';
import {Frames} from '@mintdata-wsp/mint-kit/api/values/frames/Frames';

export class NamedRangesController
{
    @action.bound
    public onRowsInserted(sheetId:SheetId, startRow:number, rowsCount:number)
    {
        this.getNamedRangesBySheetId(sheetId)
            .filter(range => range.location.rowIndex >= startRow)
            .forEach(range => range.location = range.location.translate(0, rowsCount));
    }

    @action.bound
    public onColsInserted(sheetId:SheetId, startCol:number, colsCount:number)
    {
        this.getNamedRangesBySheetId(sheetId)
            .filter(range => range.location.colIndex >= startCol)
            .forEach(range => range.location = range.location.translate(colsCount, 0));
    }

    @action.bound
    public onRowsDeleted(sheetId:SheetId, startRow:number, rowsCount:number, keepRanges:boolean)
    {
        const endRow = startRow + rowsCount;
        for( const range of this.getNamedRangesBySheetId(sheetId) )
        {
            const rowIndex = range.location.rowIndex;
            const minRowIndexToMove = keepRanges ? startRow : endRow;

            if( rowIndex >= minRowIndexToMove )
                range.location = range.location.translate(0, -rowsCount);
            else if( !keepRanges && rowIndex >= startRow )
                this.removeNamedRange(range.id);
        }
    }

    @action.bound
    public onColsDeleted(sheetId:SheetId, startCol:number, colsCount:number, keepRanges:boolean)
    {
        const endCol = startCol + colsCount;
        for( const range of this.getNamedRangesBySheetId(sheetId) )
        {
            const colIndex = range.location.colIndex;
            const minColIndexToMove = keepRanges ? startCol : endCol;

            if( colIndex >= minColIndexToMove )
                range.location = range.location.translate(-colsCount, 0);
            else if( !keepRanges && colIndex >= startCol )
                this.removeNamedRange(range.id);
        }
    }

    @bind
    public copyNamedRange(range:NamedRange):NamedRange
    {
        Assert.no(range.name, 'only unnamed ranges can be cloned currently');

        const newRange = range.clone();
        this._getRuntimeState().addNamedRange(newRange);
        return newRange;
    }

    @bind
    public createNamedRange(workbookId:WorkbookId, sheetId:SheetId, location:SlotLocation, framePath:FramePath, rowCount:number, colCount:number, name?:string):NamedRange
    {
        const newRange = NamedRange.create(name, sheetId, location, framePath, rowCount, colCount);

        this._getRuntimeState().addNamedRange(newRange);

        return newRange;
    }

    public getNamedRangesByLocation(sheetId:SheetId, location:SlotLocation):Array<NamedRange>
    {
        return this.getNamedRangesBySheetId(sheetId)
            .filter(range => range.location.isEqualTo(location));
    }

    @bind
    public getNamedRangesBySheetId(sheetId:SheetId):Array<NamedRange>
    {
        const runtimeState = this._getRuntimeState();
        return runtimeState.namedRanges.filter(r => r.sheetId == sheetId);
    }

    @bind
    public getNamedRangeById(id:NamedRangeId):NamedRange | null
    {
        const runtimeState = this._getRuntimeState();
        return runtimeState.getNameRangeById(id);
    }

    @bind
    public getNamedRangeByName(name:string):NamedRange | null
    {
        const runtimeState = this._getRuntimeState();
        return runtimeState.getNameRangeByName(name);
    }

    @action.bound
    public removeNamedRange(id:NamedRangeId):void
    {
        const {bindings} = commonControllers;

        const runtimeState = this._getRuntimeState();

        const range = this.getNamedRangeById(id);
        if( range )
        {
            runtimeState.deleteNamedRange(id);
            bindings.onNamedRangeRemoved(range);
        }
    }

    @action.bound
    public removeNamedRangeBySheetId(sheetId:SheetId):void
    {
        this.getNamedRangesBySheetId(sheetId).forEach(range =>
            this.removeNamedRange(range.id)
        );
    }

    @bind
    public getNamedRangeSize(id:NamedRangeId):RangeSize | AError
    {
        const range = this.getNamedRangeById(id);
        Assert.yes(range, 'range should exist here');

        if( range.framePath )
            return this._getNamedRangeSizeFromFrame(range);

        const {sheetId, location} = range;

        const sheetCtrl = this._getSheetCtrl(sheetId);
        return this._computeRangeSize(
            range,
            location.rowIndex, location.colIndex,
            Number.MAX_VALUE, Number.MAX_VALUE,
            (ri, ci) => sheetCtrl.getSlotValue(sheetId, new SlotLocation(ci, ri))
        );
    }

    @bind
    private _getNamedRangeSizeFromFrame(range:NamedRange):RangeSize | AError
    {
        const pathElementsOrError = this._getRangeRepeatedFramePathElements(range, Repeater.initial(), RepeaterFixation.FixedBoth);
        if( Type.isError(pathElementsOrError) )
            return pathElementsOrError as AError;

        const pathElements = pathElementsOrError as FramePathElements;
        const {frame, rowIndex, colIndex} = pathElements[pathElements.length - 1];

        let data;
        if( Type.isInstanceOf(frame, Type.Frame) )
            data = (frame as IFrame).getDataUnsafe();
        else if( Type.isInstanceOf(frame, Type.Error) )
            data = Frames.forErrorWithContext(frame as AError, (frame as AError).errorContext).getDataUnsafe();

        return this._computeRangeSize(
            range,
            rowIndex, colIndex,
            Number.MAX_VALUE, Number.MAX_VALUE,
            (ri, ci) => ri < data.length && data[ri][ci] || null
        );
    }

    @bind
    // returns at most rowsLimit rows and colsLimit columns (but can return less, if range is not big enough)
    public getNamedRangeValues(id:NamedRangeId,
                               repeater:Repeater, fixation:RepeaterFixation,
                               useFormat:boolean,
                               rowsLimit:number, colsLimit:number):Array<Array<IValue>> | AError
    {
        Assert.yes(rowsLimit && rowsLimit >= 0, 'rowsLimit should not be negative');
        Assert.yes(colsLimit && colsLimit >= 0, 'colsLimit should not be negative');

        const range = this.getNamedRangeById(id);
        Assert.yes(range, 'range should exist here');

        if( range.framePath )
            return this._getNamedRangeValuesFromFrame(range, repeater, fixation, useFormat, rowsLimit, colsLimit);

        const {sheetId, location} = range;
        const sheetCtrl = this._getSheetCtrl(sheetId);
        const sheetSettingsCtrl = this._getSheetSettingsCtrl(sheetId);

        const rowOffset = EnumUtils.isFixedDown(fixation) ? 0 : repeater.rowOffset;
        const colOffset = EnumUtils.isFixedRight(fixation) ? 0 : repeater.colOffset;

        const valueFn = (ri, ci) =>
            sheetCtrl.getSlotValue(sheetId, new SlotLocation(ci, ri));

        const formatterFn = (ri, ci, value) =>
        {
            if( value )
            {
                const cellKey = MeshLocation.createKey(ci, ri);
                const cellFormat = sheetSettingsCtrl.getCellFormat(sheetId, cellKey);
                if( cellFormat )
                {
                    const cellSettings = sheetSettingsCtrl.getCellSettings(sheetId, cellKey);
                    return this._formatValue(cellFormat, cellSettings, value);
                }
            }
            return value;
        };

        return this._collectRangeValues(
            range,
            location.rowIndex + rowOffset, location.colIndex + colOffset,
            rowsLimit, colsLimit,
            valueFn,
            useFormat ? formatterFn : null
        );
    }

    @bind
    private _getNamedRangeValuesFromFrame(range:NamedRange,
                                          repeater:Repeater, fixation:RepeaterFixation,
                                          useFormat:boolean,
                                          rowsLimit:number, colsLimit:number):Array<Array<IValue>> | AError
    {
        const {sheetId, location, framePath} = range;
        Assert.yes(framePath && framePath.length > 0, 'framePath should not be empty here');

        const sheetCtrl = this._getSheetCtrl(sheetId);
        const slot = sheetCtrl.findSlot(sheetId, location);
        if( !slot || !(Type.isInstanceOf(slot.value, Type.Frame) || Type.isInstanceOf(slot.value, Type.Error)) )
            return AError.fromString('#InvalidNestedFrame!');

        const pathElementsOrError = repeater.getRepeatedFramePathElements(slot.value as IFrame, framePath, fixation);
        if( Type.isError(pathElementsOrError) )
            return pathElementsOrError as AError;

        const pathElements = pathElementsOrError as FramePathElements;
        const {frame, rowIndex, colIndex} = pathElements[pathElements.length - 1];

        let data;
        if( Type.isInstanceOf(frame, Type.Frame) )
            data = (frame as IFrame).getDataUnsafe();
        else if( Type.isInstanceOf(frame, Type.Error) )
            data = Frames.forErrorWithContext(frame as AError, (frame as AError).errorContext).getDataUnsafe();

        const valueFn = (ri, ci) =>
            ri < data.length && data[ri][ci] || null;

        const createFormatterFn = () =>
        {
            if( !useFormat )
                return null;

            const framePathPrefix = pathElements.slice(0, pathElements.length - 1);

            const sheetSettingsCtrl = this._getSheetSettingsCtrl(sheetId);
            const sheetSettingsId = sheetSettingsCtrl.buildSheetSettingsIdBySlotId(slot.id, framePathPrefix);

            return (ri, ci, value) =>
            {
                if( value )
                {
                    const key = MeshLocation.createKey(ci, ri);
                    const cellFormat = sheetSettingsCtrl.getCellFormatBySheetSettingsId(sheetSettingsId, key);
                    if( cellFormat )
                    {
                        const cellSettings = sheetSettingsCtrl.getCellSettingsBySheetSettingsId(sheetSettingsId, key);
                        return this._formatValue(cellFormat, cellSettings, value);
                    }
                }
                return value;
            };
        };

        return this._collectRangeValues(
            range,
            rowIndex, colIndex,
            rowsLimit, colsLimit,
            valueFn,
            createFormatterFn()
        );
    }

    @bind
    private _getRangeRepeatedFramePathElements(range:NamedRange, repeater:Repeater, fixation:RepeaterFixation):FramePathElements | AError
    {
        const {sheetId, location, framePath} = range;
        Assert.yes(framePath && framePath.length > 0, 'framePath should not be empty here');

        const sheetCtrl = this._getSheetCtrl(sheetId);

        const value = sheetCtrl.getSlotValue(sheetId, location);
        if( !(Type.isInstanceOf(value, Type.Frame) || Type.isInstanceOf(value, Type.Error)) )
            return AError.fromString('#InvalidNestedFrame!');

        return repeater.getRepeatedFramePathElements(value as IFrame, framePath, fixation);
    }

    @bind
    private _collectRangeValues(range:NamedRange,
                                startRow:number, startCol:number,
                                rowsLimit:number, colsLimit:number,
                                valueFn:(rowIndex, colIndex) => IValue,
                                formatterFn?:(rowIndex, colIndex, value) => IValue):Array<Array<IValue>>
    {
        const rangeSize = this._computeRangeSize(
            range,
            startRow, startCol,
            rowsLimit, colsLimit,
            valueFn
        );

        return _times(rangeSize.rowCount, rowIndex =>
            _times(rangeSize.colCount, colIndex =>
            {
                let value = valueFn(startRow + rowIndex, startCol + colIndex);
                if( formatterFn )
                    value = formatterFn(startRow + rowIndex, startCol + colIndex, value);
                return value;
            })
        );
    }

    @bind
    private _computeRangeSize(range:NamedRange,
                              startRow:number, startCol:number,
                              rowsLimit:number, colsLimit:number,
                              valueFn:(rowIndex, colIndex) => IValue):RangeSize
    {
        let rowCount = Math.min(rowsLimit, range.rowCount);
        if( rowCount <= 0 )
        {
            // auto rows
            rowCount = 0;
            while( !BaseValue.isValueEmpty(valueFn(startRow + rowCount, startCol)) )
                rowCount++;
        }

        let colCount = Math.min(colsLimit, range.colCount);
        if( colCount <= 0 )
        {
            // auto cols
            colCount = 0;
            while( !BaseValue.isValueEmpty(valueFn(startRow, startCol + colCount)) )
                colCount++;
        }

        return {rowCount, colCount};
    }

    private _formatValue(cellFormat:IMeshCellValueFormat, cellSettings:MeshCellSettings, value:IValue)
    {
        let formattedValue;
        if( cellFormat )
        {
            if( cellSettings && cellSettings.isValueMasked )
                formattedValue = MeshCellValueFormat.MaskedText.formatValue(value);
            else
                formattedValue = cellFormat.formatValue(value);
        }
        else
        {
            formattedValue = MeshCellValueFormat.General.formatValue(value);
        }
        return AString.fromString(formattedValue ? formattedValue.toString() : '');
    }

    @bind
    private _getSheetCtrl(sheetId:SheetId):AbstractSheetController
    {
        return commonControllers.spreadsheets.workbook.getSheetCtrl(sheetId);
    }

    @bind
    private _getSheetSettingsCtrl(sheetId:SheetId):AbstractSheetSettingsController
    {
        return commonControllers.spreadsheets.workbook.getSheetSettingsCtrl(sheetId);
    }

    @bind
    protected _getRuntimeState():SpreadsheetRuntimeState
    {
        const {main} = commonControllers.spreadsheets;
        const workbook = main.getRootWorkbook();

        if( !workbook )
            return null;

        return workbook.runtime;
    }
}
